import { useEffect, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Cell,
} from "recharts";
import { getStatsDB } from "@/lib/supabaseServices";
import { Trophy, Percent, Scale, Layers, Loader2, Zap, Target } from "lucide-react";
import SessionMatrix from "@/components/SessionMatrix";
import CalendarHeatmap from "@/components/CalendarHeatmap";

const GRADE_COLORS = { "A+": "#00FF41", A: "#00FF41", B: "#FFD700", C: "#FF7A00", D: "#FF003C" };

function Counter({ value, decimals = 0, suffix = "" }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    const controls = animate(mv, value, {
      duration: 1,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    });
    return controls.stop;
  }, [value]);
  return (
    <span>
      {display}
      {suffix}
    </span>
  );
}

const StatCard = ({ icon: Icon, label, value, color, decimals, suffix }) => (
  <div className="border border-[#1A1A24] bg-[#0C0C0E] p-6 relative overflow-hidden">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] tracking-[0.2em] uppercase text-[#848D97]">{label}</span>
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <div className="font-display font-black text-4xl" style={{ color, textShadow: `0 0 18px ${color}55` }}>
      <Counter value={value} decimals={decimals} suffix={suffix} />
    </div>
  </div>
);

const tooltipStyle = {
  background: "#0C0C0E",
  border: "1px solid #1A1A24",
  color: "#E2E8F0",
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 12,
};

export default function StatsDashboard({ refreshKey }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getStatsDB()
      .then((data) => setStats(data))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading)
    return (
      <div className="flex items-center justify-center py-32 text-[#00F0FF]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );

  const empty = !stats || stats.total === 0;

  return (
    <div className="space-y-8" data-testid="stats-dashboard">
      <div>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-white leading-none">
          PERFORMANCE <span className="text-[#00F0FF] text-glow-cyan">MATRIX</span>
        </h1>
        <p className="text-[#848D97] text-sm mt-2">Your edge, quantified.</p>
      </div>

      {empty ? (
        <div className="border border-dashed border-[#1A1A24] py-24 text-center text-[#848D97]">
          No trades logged yet. Log your first setup to populate the matrix.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Layers} label="Total Trades" value={stats.total} color="#00F0FF" />
            <StatCard
              icon={Percent}
              label="Win Rate"
              value={stats.win_rate}
              color={stats.wins + stats.losses > 0 ? "#00FF41" : "#848D97"}
              decimals={1}
              suffix="%"
            />
            <StatCard icon={Scale} label="Avg R:R" value={stats.avg_rr} color="#FFD700" decimals={2} />
            <StatCard
              icon={Trophy}
              label="Wins / Losses"
              value={stats.wins}
              color="#00FF41"
              suffix={` / ${stats.losses}`}
            />
          </div>

          {/* 🏆 Trading Session Matrix */}
          <SessionMatrix sessionMatrix={stats?.session_matrix} />

          {/* 📅 Monthly Calendar & Discipline Heatmap */}
          <CalendarHeatmap refreshKey={refreshKey} />

          {/* Session & Instrument Edge Heatmap */}
          {stats.heatmap && stats.heatmap.length > 0 && (
            <div className="border border-[#1A1A24] bg-[#0C0C0E] p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs tracking-[0.2em] uppercase text-[#00F0FF] flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#00F0FF]" /> Session &amp; Instrument Edge Matrix
                </h3>
                <span className="text-[10px] text-[#848D97]">Win-Rate Breakdown</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {stats.heatmap.map((item) => {
                  const wrColor =
                    item.win_rate >= 65 ? "#00FF41" : item.win_rate >= 45 ? "#FFD700" : "#FF003C";
                  return (
                    <div
                      key={item.key}
                      className="border border-[#1A1A24] bg-[#050505] p-3 space-y-1 hover:border-[#00F0FF]/40 transition-colors"
                    >
                      <div className="text-xs font-bold text-white uppercase">{item.key}</div>
                      <div className="flex items-center justify-between text-xs pt-1">
                        <span className="text-[#848D97]">Win Rate:</span>
                        <span className="font-bold font-mono" style={{ color: wrColor }}>
                          {item.win_rate}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-[#848D97]">
                        <span>Trades:</span>
                        <span>
                          {item.wins}W / {item.losses}L ({item.total} total)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-[#1A1A24] bg-[#0C0C0E] p-6">
              <h3 className="text-xs tracking-[0.2em] uppercase text-[#848D97] mb-6">ICC Grade Distribution</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.grade_dist}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1A1A24" vertical={false} />
                  <XAxis dataKey="grade" stroke="#848D97" tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} />
                  <YAxis stroke="#848D97" allowDecimals={false} tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(0,240,255,0.05)" }} />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]} animationDuration={900}>
                    {stats.grade_dist.map((d) => (
                      <Cell key={d.grade} fill={GRADE_COLORS[d.grade] || "#00F0FF"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="border border-[#1A1A24] bg-[#0C0C0E] p-6">
              <h3 className="text-xs tracking-[0.2em] uppercase text-[#848D97] mb-6">R-Multiple Equity Curve</h3>
              {stats.curve.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={stats.curve.map((c, i) => ({ ...c, idx: i + 1 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1A1A24" />
                    <XAxis dataKey="idx" stroke="#848D97" tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} />
                    <YAxis stroke="#848D97" tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#00F0FF"
                      strokeWidth={2}
                      dot={{ fill: "#00F0FF", r: 3 }}
                      animationDuration={1200}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[260px] text-[#848D97] text-sm">
                  Mark trades as win/loss to build your curve.
                </div>
              )}
            </div>
          </div>

          <div className="border border-[#1A1A24] bg-[#0C0C0E] p-6">
            <h3 className="text-xs tracking-[0.2em] uppercase text-[#848D97] mb-6">Trades by Session</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.session_dist} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1A1A24" horizontal={false} />
                <XAxis type="number" stroke="#848D97" allowDecimals={false} tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} />
                <YAxis
                  type="category"
                  dataKey="session"
                  stroke="#848D97"
                  width={90}
                  tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }}
                />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(0,240,255,0.05)" }} />
                <Bar dataKey="count" fill="#00F0FF" radius={[0, 2, 2, 0]} animationDuration={900} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
